import { InspectionValues, LotEventTypes, LotStatus } from '@parallel-fluidics/shared/constants';
import { lotEventToFirestore } from '@parallel-fluidics/shared/models/lots';
import { travelerFromFirestore } from '@parallel-fluidics/shared/models/travelers';
import {
  collection,
  doc,
  getFirestore,
  runTransaction,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore';

import planLotQuarantine from '../utils/planLotQuarantine';
import fetchLot from './fetchLot';
import traceLotDownstream from './traceLotDownstream';

const MAX_BATCH_OPS = 450; // Firestore caps a WriteBatch at 500 writes.

const appendNote = (existing, note) => (existing ? `${existing}\n${note}` : note);

/**
 * Quarantine a lot — alone, or with everything built from it.
 *
 * The graph is re-walked fresh here (the modal's preview may be stale),
 * then per planLotQuarantine: every lot not already Quarantined gets the
 * status plus a status_changed event pointing back at the root;
 * completed travelers get their last step set to Reject: Major with the
 * note appended; in-progress and shipped travelers get the note
 * appended to each step that installed an affected bag (a note-only
 * step change on a shipped traveler doesn't trip updatedTravelerV2's
 * inventory machine) — shipped ones additionally come back in the plan
 * for customer follow-up.
 *
 * Lot writes go out in chunked batches: they're blind field patches
 * (status + timestamp) that don't depend on what was read, so
 * last-write-wins is correct for them. Traveler `steps` writes are
 * read-modify-write of a whole array, so each goes in its own one-doc
 * transaction — the doc is re-read fresh and the change re-derived
 * inside, so a concurrent edit (an install save also writes the whole
 * array) can never be reverted by a stale trace read. Neither makes the
 * overall action atomic: a failure mid-way can leave a partial
 * quarantine — re-running the action heals it (already-quarantined lots
 * and already-noted traveler steps are skipped, so a re-run is a no-op
 * for everything that already landed).
 *
 * @param {object} params
 * @param {string} params.rootLotId
 * @param {'downstream'|'single'} params.scope
 * @param {string} params.reason
 * @param {{uid: string, email: string}} params.user
 * @returns {Promise<object>} The executed plan, for the success toast.
 */
export default async function quarantineLot({
  rootLotId, scope, reason, user,
}) {
  const db = getFirestore();

  let trace;
  if (scope === 'downstream') {
    trace = await traceLotDownstream(rootLotId);
  } else {
    const root = await fetchLot(rootLotId);
    trace = { root, lotsById: { [root.id]: root }, travelersById: {} };
  }
  const plan = planLotQuarantine(trace);
  const walkLotIds = new Set(Object.keys(trace.lotsById));

  const batches = [];
  let batch = writeBatch(db);
  let ops = 0;
  const batchWithRoom = (count) => {
    if (ops + count > MAX_BATCH_OPS) {
      batches.push(batch);
      batch = writeBatch(db);
      ops = 0;
    }
    ops += count;
    return batch;
  };

  plan.lots.forEach((lot) => {
    const currentBatch = batchWithRoom(2);
    const lotRef = doc(db, 'lots', lot.id);
    currentBatch.update(lotRef, {
      status: LotStatus.QUARANTINED,
      statusChangedAt: serverTimestamp(),
    });
    currentBatch.set(doc(collection(lotRef, 'events')), lotEventToFirestore({
      type: LotEventTypes.STATUS_CHANGED,
      reason: [LotStatus.QUARANTINED, reason].filter(Boolean).join(' — '),
      ...(lot.id !== rootLotId ? { relatedIds: { lotId: rootLotId } } : {}),
      _createdBy: user,
    }, serverTimestamp));
  });

  // The trailing batch is empty when everything already landed (e.g. an
  // idempotent re-run) — skip committing it.
  if (ops > 0) batches.push(batch);
  // Sequential on purpose: if one batch fails, later ones don't fire.
  await batches.reduce(
    (previous, pending) => previous.then(() => pending.commit()),
    Promise.resolve(),
  );

  const updatedBy = { email: user.email, uid: user.uid, timestamp: serverTimestamp() };

  // Rewrite one traveler's steps in its own transaction: deriveSteps runs
  // on the freshly read array (returning null to skip), so a concurrent
  // edit conflicts and re-runs this closure on the winner's data instead
  // of being reverted. Resolves to whether anything was written.
  const rewriteTravelerSteps = (travelerId, deriveSteps) => runTransaction(
    db,
    async (transaction) => {
      const travelerRef = doc(db, 'travelers', travelerId);
      const travelerSnap = await transaction.get(travelerRef);
      if (!travelerSnap.exists()) return false;
      const freshTraveler = travelerFromFirestore(travelerSnap);
      const newSteps = deriveSteps(freshTraveler.steps || []);
      if (!newSteps) return false;
      transaction.update(travelerRef, { steps: newSteps, _updatedBy: updatedBy });
      return true;
    },
  );

  // Sequential like the batches — if one traveler fails, later ones don't
  // fire. Returns the entries actually written this run (note writes are
  // idempotent: a step whose inspectedNote already carries the exact note
  // is skipped, so a re-run after a mid-way failure heals the travelers
  // that were missed without spamming duplicate notes onto the rest).
  const writeSequentially = async (entries, rewrite) => {
    const written = [];
    await entries.reduce((previous, entry) => previous.then(async () => {
      if (await rewrite(entry)) written.push(entry);
    }), Promise.resolve());
    return written;
  };

  const rejected = await writeSequentially(
    plan.rejected,
    ({ traveler, affectedCodes }) => {
      const note = `Component lot(s) ${affectedCodes.join(', ')} quarantined — ${reason}`;
      return rewriteTravelerSteps(traveler.id, (steps) => {
        const lastIndex = steps.length - 1;
        if (lastIndex < 0 || steps[lastIndex].inspectedNote?.includes(note)) return null;
        const newSteps = [...steps];
        newSteps[lastIndex] = {
          ...newSteps[lastIndex],
          inspected: InspectionValues.REJECT_MAJOR,
          inspectedNote: appendNote(newSteps[lastIndex].inspectedNote, note),
        };
        return newSteps;
      });
    },
  );

  // In-progress travelers get flagged where the bag went in; shipped
  // travelers get the same note as the audit trail (recall happens with
  // the customer, but the record should say why). Deriving from the fresh
  // steps also flags installs that landed while this action was running.
  const deriveFlagSteps = (steps) => {
    let touched = false;
    const newSteps = steps.map((step) => {
      const stepCodes = (step.installedLots || [])
        .filter((entry) => walkLotIds.has(entry.lotId))
        .map((entry) => trace.lotsById[entry.lotId].code || entry.lotId);
      if (!stepCodes.length) return step;
      const note = `Component lot(s) ${stepCodes.join(', ')} quarantined — ${reason}`;
      if (step.inspectedNote?.includes(note)) return step;
      touched = true;
      return { ...step, inspectedNote: appendNote(step.inspectedNote, note) };
    });
    return touched ? newSteps : null;
  };
  const flagTraveler = ({ traveler }) => rewriteTravelerSteps(traveler.id, deriveFlagSteps);
  const flagged = await writeSequentially(plan.flagged, flagTraveler);
  const shipped = await writeSequentially(plan.shipped, flagTraveler);

  return {
    lots: plan.lots, rejected, flagged, shipped,
  };
}
